import { Controller } from '@hotwired/stimulus'

export default class ScheduleDayController extends Controller {
  static targets = ['canvas', 'breakContainer', 'startTimeInput', 'endTimeInput', 'closedCheckbox', 'timeLabel']
  static values  = { companyStart: Number, companyEnd: Number }

  connect() {
    const sv = this.hasStartTimeInputTarget ? parseInt(this.startTimeInputTarget.value) : NaN
    const ev = this.hasEndTimeInputTarget   ? parseInt(this.endTimeInputTarget.value)   : NaN
    this.openStart = (!isNaN(sv) && sv > 0) ? sv : null
    this.openEnd   = (!isNaN(ev) && ev > 0) ? ev : null

    this.breaks = []
    if (this.hasBreakContainerTarget) {
      this.breakContainerTarget.querySelectorAll('.brk').forEach(el => {
        const bid = el.dataset.bid
        const sEl = el.querySelector('[data-f="s"]')
        const eEl = el.querySelector('[data-f="e"]')
        const dEl = el.querySelector('[data-f="d"]')
        if (!sEl || !eEl) return
        const s = parseInt(sEl.value)
        const e = parseInt(eEl.value)
        if ((!dEl || dEl.value !== '1') && !isNaN(s) && !isNaN(e) && e > s) {
          this.breaks.push({ id: bid, start: s, end: e })
        }
      })
    }

    this.drag   = null
    this.barEl  = null
    this.hoverX = null

    // Use Pointer Events — handles mouse and touch uniformly.
    // Listeners live on the canvas (which persists through renders), so
    // setPointerCapture keeps working even after the bar DOM is rebuilt.
    this._onPointerMove   = this.onPointerMove.bind(this)
    this._onPointerUp     = this.onPointerUp.bind(this)
    this._onPointerCancel = this.onPointerCancel.bind(this)

    if (this.hasCanvasTarget) {
      this.canvasTarget.addEventListener('pointermove',   this._onPointerMove)
      this.canvasTarget.addEventListener('pointerup',     this._onPointerUp)
      this.canvasTarget.addEventListener('pointercancel', this._onPointerCancel)
    }

    this._ro = new ResizeObserver(() => this.render())
    if (this.hasCanvasTarget) this._ro.observe(this.canvasTarget)

    const markers = document.getElementById('schedule-hour-markers')
    if (markers && !markers._scheduleDayObserver) {
      markers._scheduleDayObserver = true
      this._ro.observe(markers)
    }

    this.render()
  }

  disconnect() {
    if (this.hasCanvasTarget) {
      this.canvasTarget.removeEventListener('pointermove',   this._onPointerMove)
      this.canvasTarget.removeEventListener('pointerup',     this._onPointerUp)
      this.canvasTarget.removeEventListener('pointercancel', this._onPointerCancel)
    }
    if (this._ro) this._ro.disconnect()
  }

  // ── Getters ──────────────────────────────────────────────────────────────

  get closed() { return this.hasClosedCheckboxTarget && this.closedCheckboxTarget.checked }
  get csVal()  { return this.companyStartValue || 600 }
  get ceVal()  { return this.companyEndValue   || 2345 }
  get BAR_H()  { return 36 }

  // ── Pointer helper ───────────────────────────────────────────────────────

  // One handler for mouse + touch + pen
  onPointer(el, fn) { el.addEventListener('pointerdown', fn) }

  // ── Coordinate Math ──────────────────────────────────────────────────────

  toMins(t)    { return !t || isNaN(t) ? 0 : Math.floor(t / 100) * 60 + (t % 100) }
  fromMins(m)  { return Math.floor(m / 60) * 100 + (m % 60) }
  totalMins()  { return this.toMins(this.ceVal) - this.toMins(this.csVal) }
  toPx(t, W)   { return ((this.toMins(t) - this.toMins(this.csVal)) / this.totalMins()) * W }

  fromPx(x, W) {
    const raw     = (x / W) * this.totalMins() + this.toMins(this.csVal)
    const clamped = Math.max(this.toMins(this.csVal), Math.min(this.toMins(this.ceVal), raw))
    return this.fromMins(Math.round(clamped / 15) * 15)
  }

  formatTime(t) {
    const h   = Math.floor(t / 100)
    const min = t % 100
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h
    const sfx = h < 12 ? 'am' : 'pm'
    return min === 0 ? `${h12}${sfx}` : `${h12}:${String(min).padStart(2, '0')}${sfx}`
  }

  // ── Render ───────────────────────────────────────────────────────────────

  render() {
    if (!this.hasCanvasTarget) return
    const canvas = this.canvasTarget
    canvas.innerHTML = ''
    canvas.style.cssText = 'flex:1;position:relative;overflow:visible;touch-action:none;'

    const W = canvas.clientWidth
    if (W < 10) return

    this.buildBar(canvas, W)

    if (this.hoverX != null && !this.drag) {
      const hx = Math.max(0, Math.min(W, this.hoverX))
      canvas.appendChild(this.makeTip(this.formatTime(this.fromPx(hx, W)), hx, 25))
      const hair = document.createElement('div')
      hair.style.cssText = `position:absolute;top:0;left:${hx}px;width:1px;height:${this.BAR_H}px;background:rgba(15,23,42,.2);pointer-events:none;z-index:5;`
      canvas.appendChild(hair)
    } else if (this.drag?.tip != null) {
      const tx = Math.max(24, Math.min(W - 24, this.drag.tipX || 0))
      canvas.appendChild(this.makeTip(this.drag.tip, tx, 30))
    }

    this.syncTimeLabel()
    this.adaptMarkers()
  }

  // Floating time label shown above cursor or drag handle
  makeTip(text, x, zIndex) {
    const el = document.createElement('div')
    el.textContent = text
    el.style.cssText = `position:absolute;top:-22px;left:${x}px;transform:translateX(-50%);background:#1e293b;color:#fff;font-size:10px;padding:1px 5px;border-radius:3px;white-space:nowrap;pointer-events:none;z-index:${zIndex};`
    return el
  }

  adaptMarkers() {
    const markers = document.getElementById('schedule-hour-markers')
    if (!markers) return
    const W    = markers.clientWidth
    const step = W < 200 ? 4 : W < 320 ? 3 : W < 480 ? 2 : 1
    markers.querySelectorAll('span[data-offset]').forEach(span => {
      span.style.display = parseInt(span.dataset.offset) % step === 0 ? '' : 'none'
    })
  }

  buildBar(canvas, W) {
    const bar = document.createElement('div')
    // touch-action:none tells the browser not to scroll/zoom on this element
    bar.style.cssText = `position:relative;height:${this.BAR_H}px;width:100%;border-radius:4px;overflow:hidden;background:#f1f5f9;cursor:crosshair;touch-action:none;`
    this.barEl = bar

    // Hour grid lines
    const startH = Math.floor(this.csVal / 100)
    const endH   = Math.ceil(this.ceVal / 100)
    for (let h = startH; h <= endH; h++) {
      const mins = this.toMins(h * 100) - this.toMins(this.csVal)
      if (mins <= 0 || mins >= this.totalMins()) continue
      const line = document.createElement('div')
      line.dataset.role = 'grid'
      line.style.cssText = `position:absolute;top:0;bottom:0;left:${(mins / this.totalMins()) * 100}%;width:1px;background:rgba(0,0,0,.07);pointer-events:none;`
      bar.appendChild(line)
    }

    // Hover tracking — mouse only (pointerType check keeps touch from triggering it)
    bar.addEventListener('pointermove', e => {
      if (this.drag || e.pointerType !== 'mouse') return
      this.hoverX = e.clientX - bar.getBoundingClientRect().left
      this.render()
    })
    bar.addEventListener('pointerleave', e => {
      if (this.drag || e.pointerType !== 'mouse') return
      this.hoverX = null
      this.render()
    })

    if (this.closed) {
      bar.appendChild(this.makeClosedOverlay())
      canvas.appendChild(bar)
      return
    }

    if (!this.openStart || !this.openEnd) {
      if (this.drag?.type === 'create-open' && this.drag.preview) {
        const { start, end } = this.drag.preview
        const pl = this.toPx(start, W)
        const pw = this.toPx(end, W) - pl
        if (pw > 0) {
          const prev = document.createElement('div')
          prev.style.cssText = `position:absolute;top:0;bottom:0;left:${pl}px;width:${pw}px;background:rgba(74,222,128,.5);border-radius:3px;pointer-events:none;`
          bar.appendChild(prev)
        }
      }

      const hint = document.createElement('div')
      hint.textContent = 'Drag to set open hours'
      hint.style.cssText = 'position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:11px;color:#9ca3af;pointer-events:none;'
      bar.appendChild(hint)

      this.onPointer(bar, e => { if (e.button === 0) this.startDrag(e, 'create-open') })
      canvas.appendChild(bar)
      return
    }

    this.renderOpenBlock(bar, W)

    this.onPointer(bar, e => {
      if (e.button !== 0) return
      if (e.target === bar || e.target.dataset.role === 'grid') this.startDrag(e, 'create-open')
    })

    canvas.appendChild(bar)
  }

  makeClosedOverlay() {
    const frag = document.createDocumentFragment()
    const stripe = document.createElement('div')
    stripe.style.cssText = 'position:absolute;inset:0;background:repeating-linear-gradient(45deg,#d1d5db,#d1d5db 4px,#f3f4f6 4px,#f3f4f6 12px);pointer-events:none;'
    const label = document.createElement('div')
    label.textContent = 'CLOSED'
    label.style.cssText = 'position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;letter-spacing:.1em;color:#9ca3af;pointer-events:none;'
    frag.append(stripe, label)
    return frag
  }

  renderOpenBlock(bar, W) {
    const left  = Math.max(0, Math.min(W, this.toPx(this.openStart, W)))
    const right = Math.max(0, Math.min(W, this.toPx(this.openEnd,   W)))
    const width = right - left
    if (width <= 0) return

    const block = document.createElement('div')
    block.style.cssText = `position:absolute;top:0;bottom:0;left:${left}px;width:${width}px;background:linear-gradient(135deg,#4ade80,#16a34a);cursor:crosshair;`

    const activeBreaks = this.breaks.filter(b => {
      if (!this.hasBreakContainerTarget) return true
      const dEl = this.breakContainerTarget.querySelector(`.brk[data-bid="${b.id}"] [data-f="d"]`)
      return !dEl || dEl.value !== '1'
    })
    activeBreaks.forEach(brk => {
      const el = this.buildBreakEl(brk, left, right, W)
      if (el) block.appendChild(el)
    })

    if (this.drag?.type === 'create-break' && this.drag.preview) {
      const { start, end } = this.drag.preview
      const pl = this.toPx(start, W) - left
      const pw = this.toPx(end, W) - this.toPx(start, W)
      if (pw > 0) {
        const prev = document.createElement('div')
        prev.style.cssText = `position:absolute;top:0;bottom:0;left:${pl}px;width:${pw}px;background:rgba(220,38,38,.5);pointer-events:none;border-radius:2px;`
        block.appendChild(prev)
      }
    }

    const lHandle = this.makeResizeHandle('left',  'resize-left')
    const rHandle = this.makeResizeHandle('right', 'resize-right')
    block.append(lHandle, rHandle)

    this.onPointer(block, e => {
      if (e.button !== 0) return
      if (!lHandle.contains(e.target) && !rHandle.contains(e.target) && !e.target.closest('button')) {
        this.startDrag(e, 'create-break')
      }
    })

    bar.appendChild(block)
  }

  buildBreakEl(brk, left, right, W) {
    const bVisL = Math.max(left, Math.min(right, this.toPx(brk.start, W)))
    const bVisR = Math.max(left, Math.min(right, this.toPx(brk.end,   W)))
    if (bVisR <= bVisL) return null

    const bl = bVisL - left
    const bw = bVisR - bVisL

    const bEl = document.createElement('div')
    bEl.style.cssText = `position:absolute;top:0;bottom:0;left:${bl}px;width:${bw}px;background:linear-gradient(135deg,#f87171,#dc2626);cursor:default;`

    if (bw > 44) {
      const blbl = document.createElement('div')
      blbl.textContent = `${this.formatTime(brk.start)}–${this.formatTime(brk.end)}`
      blbl.style.cssText = 'position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:600;color:#fff;pointer-events:none;text-shadow:0 1px 2px rgba(0,0,0,.3);'
      bEl.appendChild(blbl)
    }

    const xBtn = document.createElement('button')
    xBtn.type = 'button'
    xBtn.textContent = '×'
    xBtn.style.cssText = 'position:absolute;top:2px;right:10px;width:14px;height:14px;border:none;background:rgba(0,0,0,.35);color:#fff;border-radius:50%;font-size:12px;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0;'
    xBtn.addEventListener('click', e => { e.stopPropagation(); this.removeBreak(brk.id) })
    bEl.appendChild(xBtn)

    bEl.append(
      this.makeResizeHandle('left',  'resize-break-left',  brk.id),
      this.makeResizeHandle('right', 'resize-break-right', brk.id)
    )
    this.onPointer(bEl, e => e.stopPropagation())

    return bEl
  }

  // Shared factory for the left/right resize handles on open blocks and breaks.
  // Break handles (brkId != null) are slightly narrower and lower z-index.
  makeResizeHandle(side, dragType, brkId = null) {
    const isBreak  = brkId != null
    const width    = isBreak ? 8 : 10
    const zIndex   = isBreak ? 2 : 3
    const bgAlpha  = isBreak ? '.2' : '.18'
    const clrAlpha = isBreak ? '.8' : '.9'
    const fontSize = isBreak ? 8  : 9

    const h = document.createElement('div')
    h.style.cssText = `position:absolute;top:0;bottom:0;${side}:0;width:${width}px;cursor:ew-resize;display:flex;align-items:center;justify-content:center;z-index:${zIndex};background:rgba(0,0,0,${bgAlpha});touch-action:none;`
    h.innerHTML = `<span style="color:rgba(255,255,255,${clrAlpha});font-size:${fontSize}px;letter-spacing:-1px;user-select:none;">⋮</span>`
    this.onPointer(h, e => { e.stopPropagation(); this.startDrag(e, dragType, brkId) })
    return h
  }

  // ── Drag ─────────────────────────────────────────────────────────────────

  startDrag(e, type, brkId = null) {
    if (this.drag) return
    e.preventDefault()
    if (!this.barEl) return

    // Capture the pointer to the canvas so all subsequent pointermove/pointerup
    // events are dispatched there — even after the bar DOM is rebuilt by render()
    if (this.hasCanvasTarget && e.pointerId != null) {
      this.canvasTarget.setPointerCapture(e.pointerId)
    }

    this.hoverX  = null
    const rect   = this.barEl.getBoundingClientRect()
    this.drag    = { type, brkId, rect, W: rect.width, startX: e.clientX - rect.left, preview: null, tip: null, tipX: null }

    document.body.style.cursor     = type.includes('resize') ? 'ew-resize' : 'crosshair'
    document.body.style.userSelect = 'none'
  }

  onPointerMove(e) {
    if (!this.drag) return
    const { type, rect, W, startX, brkId } = this.drag
    const t      = this.fromPx(e.clientX - rect.left, W)
    const tStart = this.fromPx(startX, W)

    if (type === 'create-open') {
      const s = Math.min(tStart, t), end = Math.max(tStart, t)
      this.drag.preview = { start: s, end }
      this.drag.tip     = `${this.formatTime(s)}–${this.formatTime(end)}`
      this.drag.tipX    = (this.toPx(s, W) + this.toPx(end, W)) / 2

    } else if (type === 'resize-left') {
      this.openStart = Math.min(t, this.openEnd ? this.fromMins(this.toMins(this.openEnd) - 15) : t)
      this.drag.tip  = this.formatTime(this.openStart)
      this.drag.tipX = this.toPx(this.openStart, W)

    } else if (type === 'resize-right') {
      this.openEnd   = Math.max(t, this.openStart ? this.fromMins(this.toMins(this.openStart) + 15) : t)
      this.drag.tip  = this.formatTime(this.openEnd)
      this.drag.tipX = this.toPx(this.openEnd, W)

    } else if (type === 'create-break') {
      const s   = Math.max(Math.min(tStart, t), this.openStart || tStart)
      const end = Math.min(Math.max(tStart, t), this.openEnd   || t)
      if (end > s) {
        this.drag.preview = { start: s, end }
        this.drag.tip     = `${this.formatTime(s)}–${this.formatTime(end)}`
        this.drag.tipX    = (this.toPx(s, W) + this.toPx(end, W)) / 2
      }

    } else if (type === 'resize-break-left') {
      const brk = this.breaks.find(b => b.id === brkId)
      if (brk) {
        brk.start      = Math.max(this.openStart || 0, Math.min(t, this.fromMins(this.toMins(brk.end) - 15)))
        this.drag.tip  = this.formatTime(brk.start)
        this.drag.tipX = this.toPx(brk.start, W)
      }

    } else if (type === 'resize-break-right') {
      const brk = this.breaks.find(b => b.id === brkId)
      if (brk) {
        brk.end        = Math.min(this.openEnd || 9999, Math.max(t, this.fromMins(this.toMins(brk.start) + 15)))
        this.drag.tip  = this.formatTime(brk.end)
        this.drag.tipX = this.toPx(brk.end, W)
      }
    }

    this.render()
  }

  onPointerUp(e) {
    if (!this.drag) return
    const { type, brkId } = this.drag

    if (type === 'create-open') {
      if (this.drag.preview) {
        const { start, end } = this.drag.preview
        if (this.toMins(end) - this.toMins(start) >= 15) {
          this.openStart = start
          this.openEnd   = end
          this.syncOpenDOM()
        }
      }

    } else if (type === 'resize-left' || type === 'resize-right') {
      this.syncOpenDOM()

    } else if (type === 'create-break') {
      if (this.drag.preview) {
        const { start, end } = this.drag.preview
        const withinOpen = start >= (this.openStart || 0) && end <= (this.openEnd || 0)
        if (this.toMins(end) - this.toMins(start) >= 15 && withinOpen) {
          const brk = { id: `new-${Date.now()}`, start, end }
          this.breaks.push(brk)
          this.addBreakDOM(brk)
        }
      }

    } else if (type === 'resize-break-left' || type === 'resize-break-right') {
      const brk = this.breaks.find(b => b.id === brkId)
      if (brk) this.syncBreakDOM(brk)
    }

    this.endDrag()
  }

  onPointerCancel() {
    // Finger lifted by system (e.g. incoming call) — discard drag without committing
    if (!this.drag) return
    this.endDrag()
  }

  endDrag() {
    this.drag = null
    document.body.style.cursor     = ''
    document.body.style.userSelect = ''
    this.render()
  }

  // ── DOM Sync ─────────────────────────────────────────────────────────────

  syncOpenDOM() {
    if (this.hasStartTimeInputTarget) this.startTimeInputTarget.value = this.openStart || ''
    if (this.hasEndTimeInputTarget)   this.endTimeInputTarget.value   = this.openEnd   || ''
  }

  syncBreakDOM(brk) {
    if (!this.hasBreakContainerTarget) return
    const el = this.breakContainerTarget.querySelector(`.brk[data-bid="${brk.id}"]`)
    if (!el) return
    const sEl = el.querySelector('[data-f="s"]')
    const eEl = el.querySelector('[data-f="e"]')
    if (sEl) sEl.value = brk.start
    if (eEl) eEl.value = brk.end
  }

  syncTimeLabel() {
    if (!this.hasTimeLabelTarget) return
    if (this.closed) {
      this.timeLabelTarget.textContent = 'Closed'
    } else if (this.openStart && this.openEnd) {
      this.timeLabelTarget.innerHTML = `${this.formatTime(this.openStart)}<br>${this.formatTime(this.openEnd)}`
    } else {
      this.timeLabelTarget.textContent = '—'
    }
  }

  addBreakDOM(brk) {
    if (!this.hasBreakContainerTarget) return
    const prefix = this.breakContainerTarget.dataset.np
    const idx    = Date.now()
    const div    = document.createElement('div')
    div.className   = 'brk'
    div.dataset.bid = brk.id
    div.innerHTML = `
      <input type="hidden" name="${prefix}[${idx}][start_time]" data-f="s" value="${brk.start}">
      <input type="hidden" name="${prefix}[${idx}][end_time]"   data-f="e" value="${brk.end}">
      <input type="hidden" name="${prefix}[${idx}][label]"      value="">
      <input type="hidden" name="${prefix}[${idx}][_destroy]"   data-f="d" value="false">
    `
    this.breakContainerTarget.appendChild(div)
  }

  destroyBreakDOM(id) {
    if (!this.hasBreakContainerTarget) return
    const dInput = this.breakContainerTarget.querySelector(`.brk[data-bid="${id}"] [data-f="d"]`)
    if (dInput) dInput.value = '1'
  }

  removeBreak(id) {
    this.destroyBreakDOM(id)
    this.breaks = this.breaks.filter(b => b.id !== id)
    this.render()
  }

  toggleClosed() { this.render() }
}
